import cn from 'classnames';
import type { ChatMessage } from 'models';
import { Tooltip } from 'ui';
import type { MessageDirection } from '../../types';
import { ChatMessageBubbleAttachments } from './chat-message-bubble-attachments';
import { ChatMessageBubbleImages } from './chat-message-bubble-images';
import { ChatMessageBubbleReadMore } from './chat-message-bubble-read-more';
import { ChatMessageBubbleText } from './chat-message-bubble-text';
import { ChatMessageBubbleVideos } from './chat-message-bubble-videos';
import { useChatMessageBubbleLayout, useMessageContent } from './hooks';

type P = {
  borderBottom?: boolean;
  borderTop?: boolean;
  direction: MessageDirection;
  marginBottom?: boolean;
  message: ChatMessage;
  tooltipTitle?: string;
};

const ChatMessageBubble = ({ borderBottom, borderTop, direction, marginBottom, message, tooltipTitle }: P) => {
  const { files, images, videos } = useMessageContent(message);
  const hasText = Boolean(message.text);
  const hasFiles = files.length > 0;
  const hasImages = images.length > 0;
  const hasVideos = videos.length > 0;
  const hasAttachments = hasFiles || hasImages || hasVideos;
  const tooltipPlacement = direction === 'outgoing' ? 'right' : 'left';
  const layout = useChatMessageBubbleLayout({ hasAttachments, hasText });

  return (
    <Tooltip placement={tooltipPlacement} title={tooltipTitle}>
      <div
        className={cn('chat-message-bubble', {
          'chat-message-bubble--attachment-only': layout === 'attachment-only',
          'chat-message-bubble--border-bottom': borderBottom,
          'chat-message-bubble--border-top': borderTop,
          'chat-message-bubble--incoming': direction === 'incoming',
          'chat-message-bubble--margin-bottom': marginBottom,
          'chat-message-bubble--mixed': layout === 'mixed',
          'chat-message-bubble--outgoing': direction === 'outgoing',
          'chat-message-bubble--text-only': layout === 'text-only',
        })}
      >
        <ChatMessageBubbleReadMore direction={direction} layout={layout}>
          {hasImages ? <ChatMessageBubbleImages images={images} /> : null}
          {hasVideos ? <ChatMessageBubbleVideos videos={videos} /> : null}
          {hasFiles ? <ChatMessageBubbleAttachments direction={direction} files={files} /> : null}
          {/* TODO: ChatMessageBubbleLinkPreview — 4. link previews */}
          {message.text ? <ChatMessageBubbleText direction={direction} text={message.text} /> : null}
        </ChatMessageBubbleReadMore>
      </div>
    </Tooltip>
  );
};

export default ChatMessageBubble;
